import { Fragment, useMemo, type ReactNode } from "react";
import type { ChannelIndicatorResp } from "@/api/diagnostics";
import { HatTextTooltip } from "@/components/tooltip";
import { DIAGNOSTICS_GROUP_TERMS } from "./diagnosticsTerms";
import type { ChannelStructureIndex } from "./parquetChannels";
import { timingRows, type TimingRow } from "./timingRows";
import styles from "./diagnosticsTables.module.css";

// 낯선 머리글에만 설명을 붙인다. 행과 주기는 이름만으로 뜻이 분명하다.
const RECORD_COLUMNS: readonly { key: keyof TimingRow; label: string; tip?: string }[] = [
  { key: "rows", label: "행" },
  { key: "rate", label: "주기" },
  {
    key: "jitter",
    label: "간격 흔들림",
    tip: "기록 간격이 중앙값에서 얼마나 벗어나는지입니다(중앙 절대 편차 ÷ 중앙값).",
  },
  {
    key: "gap",
    label: "빈틈",
    tip: "기록 간격이 주기의 5배를 넘은 자리의 비율입니다. 샘플이 빠진 자리입니다.",
  },
  {
    key: "order",
    label: "역행·중복",
    tip: "timestamp 가 뒤로 가거나 같은 값이 거듭된 자리의 비율입니다.",
  },
  { key: "nonFinite", label: "비유한 값", tip: "NaN·무한대가 기록된 값의 비율입니다." },
  {
    key: "span",
    label: "시간 범위",
    tip: "이 채널의 첫 기록과 마지막 기록 시각입니다(에피소드 기준 초). 영상보다 먼저 시작했으면 음수입니다.",
  },
];

/**
 * 타이밍 보기. 채널마다 기록된 샘플 수·간격·공백·시간 범위를 표시한다.
 *
 * 값은 모두 진단 상세의 첫 응답(`channels[]`)에서 온다. 그래서 위상 그리드를 기다리지 않는다.
 */
export function TimingView({
  channels,
  index,
}: {
  channels: readonly ChannelIndicatorResp[];
  index: ChannelStructureIndex;
}): ReactNode {
  const records = useMemo(() => timingRows(channels, index), [channels, index]);

  return (
    <div className={styles.view}>
      <section className={styles.section} aria-label="채널 기록">
        <span className={styles.title}>채널 기록</span>
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">채널</th>
                {RECORD_COLUMNS.map((column) => (
                  <th key={column.key} scope="col">
                    {column.tip === undefined ? (
                      column.label
                    ) : (
                      <HeaderTip label={column.label} tip={column.tip} />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((row, i) => (
                <Fragment key={row.channel}>
                  {/* 그룹이 바뀌는 자리에 머리 행을 둔다. 이름이 같은 Action·Observation 채널을
                      이 머리 행이 구분한다. */}
                  {i === 0 || records[i - 1].group !== row.group ? (
                    <tr className={styles.groupRow}>
                      <th scope="rowgroup" colSpan={RECORD_COLUMNS.length + 1}>
                        {DIAGNOSTICS_GROUP_TERMS[row.group].label}
                      </th>
                    </tr>
                  ) : null}
                  <tr>
                    <th scope="row">{row.title}</th>
                    {RECORD_COLUMNS.map((column) => (
                      <td key={column.key}>{row[column.key]}</td>
                    ))}
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function HeaderTip({ label, tip }: { label: string; tip: string }): ReactNode {
  return (
    <HatTextTooltip text={tip} placement="bottom" align="start">
      <span className={styles.tipLabel}>{label}</span>
    </HatTextTooltip>
  );
}
